import staffApi, { staffApiHooks } from "@/api/staffApi";
import { useEffect, useState } from "react";
import AboutTab from "./AboutTab";
import WorkingHoursTab from "./WorkingHoursTab";
import UpdatesTab from "./UpdatesTab";
import AddStaffModal from "./AddStaffModal";
import EditStaffModal from "./EditStaffModal";
import { FiSearch, FiEdit2, FiTrash2 } from "react-icons/fi";

const TABS = ["About", "Working hours", "Updates"];

function getInitials(first: string, last: string) {
  return `${first?.[0] || ""}${last?.[0] || ""}`.toUpperCase();
}

export default function MyTeam() {
  const [selectedStaffIdx, setSelectedStaffIdx] = useState(0);
  const [selectedTab, setSelectedTab] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [showEditStaffModal, setShowEditStaffModal] = useState(false);
  const [staffList, setStaffList] = useState<any[]>([]);

  // Debounce search input - wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  // Use React Query hook with search functionality
  const {
    data: staffResponse,
    refetch: refetchStaff,
    isLoading: isSearching,
  } = staffApiHooks.useStaff(debouncedSearch || undefined);
  const rawStaffList = staffResponse?.data?.staffs || [];

  // Fetch staff pictures similar to contact.tsx
  useEffect(() => {
    async function fetchStaffPictures() {
      if (rawStaffList.length > 0) {
        console.log(
          "📸 Fetching staff pictures for",
          rawStaffList.length,
          "staff members"
        );
        try {
          // Fetch all staff pictures in parallel
          const staffWithPictures = await Promise.all(
            rawStaffList.map(async (staff: any) => {
              console.log(`📸 Processing staff ${staff.id}:`, {
                hasProfile: !!staff.profile,
                hasPicture: !!staff.profile?.picture,
                pictureValue: staff.profile?.picture,
              });

              if (staff.profile?.picture) {
                try {
                  console.log(
                    `📸 Fetching picture for staff ${staff.id}:`,
                    staff.profile.picture
                  );
                  const pictureResponse = await staffApi.getStaffPicture(
                    staff.profile.picture
                  );
                  const imageUrl = URL.createObjectURL(pictureResponse.data);
                  console.log(
                    `✅ Successfully loaded picture for staff ${staff.id}`
                  );
                  return {
                    ...staff,
                    profile: {
                      ...staff.profile,
                      pictureBlob: imageUrl,
                    },
                  };
                } catch (error) {
                  console.error(
                    `❌ Error fetching picture for staff ${staff.id}:`,
                    error
                  );
                  return staff;
                }
              }
              console.log(`ℹ️ No picture for staff ${staff.id}`);
              return staff;
            })
          );

          console.log("📸 Staff pictures processing completed");
          // Update the staff list with picture blobs
          setStaffList(staffWithPictures);
        } catch (error) {
          console.error("❌ Error fetching staff pictures:", error);
          // Fallback to using rawStaffList without pictures
          setStaffList(rawStaffList);
        }
      } else {
        // No staff data, set empty array
        setStaffList([]);
      }
    }
    fetchStaffPictures();

    // Cleanup function to revoke object URLs
    return () => {
      staffList.forEach((staff: any) => {
        if (staff.profile?.pictureBlob) {
          URL.revokeObjectURL(staff.profile.pictureBlob);
        }
      });
    };
  }, [rawStaffList]); // Only run when staff data changes

  const handleSaveWorkingHours = async (workingHours: any) => {
    try {
      await staffApi.updateStaff(selectedStaff.id, {
        ...selectedStaff,
        profile: {
          ...selectedStaff.profile,
          working_hours: workingHours,
        },
      });
      refetchStaff(); // Refresh the staff list after update
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddStaff = () => {
    setShowAddStaffModal(true);
  };

  const handleCloseAddStaffModal = () => {
    setShowAddStaffModal(false);
  };

  const handleStaffAdded = () => {
    setShowAddStaffModal(false);
    refetchStaff();
  };

  const handleEditStaff = () => {
    setShowEditStaffModal(true);
  };

  const handleCloseEditStaffModal = () => {
    setShowEditStaffModal(false);
  };

  const handleStaffUpdated = () => {
    setShowEditStaffModal(false);
    refetchStaff();
  };

  const handleDeleteStaff = async () => {
    if (!selectedStaff) return;
    if (!window.confirm("Are you sure you want to delete this staff member?"))
      return;
    try {
      await staffApi.deleteStaff(selectedStaff.id);
      // Refresh the staff list and update selection
      refetchStaff();
      setSelectedStaffIdx(Math.max(0, selectedStaffIdx - 1));
    } catch (error) {
      alert("Failed to delete staff member.");
    }
  };

  const selectedStaff = staffList[selectedStaffIdx];
  // Assume the first staff is 'You' for demo; replace with real user logic if available
  const currentUserId = staffList[0]?.id;

  // Staff list is now filtered by server-side search
  const filteredStaff = staffList;

  return (
    <div className="w-full h-full flex flex-col  overflow-auto">
      <div className="w-full h-full flex flex-col">
        <div className="flex h-full">
          {/* Staff List */}
          <div className="w-64 h-full bg-white  p-4 flex flex-col">
            <div className="flex justify-between items-center mb-2">
              <div className="font-semibold text-gray-700 text-lg">My Team</div>
              <button
                className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 hover:bg-green-100 border border-gray-200 text-green-500 text-lg"
                onClick={(e) => {
                  e.preventDefault();
                  handleAddStaff();
                }}
                title="Add Staff Member"
                aria-label="Add Staff Member"
              >
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    stroke="currentColor"
                    strokeWidth="2"
                    d="M12 8v8M8 12h8"
                  />
                </svg>
              </button>
            </div>
            {/* All/Team Tabs */}
            {/* <div className="flex mb-3 border-b border-gray-200">
              <button className="px-2 pb-1 text-base font-medium border-b-2 border-green-500 text-green-700 focus:outline-none">
                All
              </button>
              <button className="px-2 pb-1 text-base font-medium border-b-2 border-transparent text-gray-500 hover:text-green-600 focus:outline-none">
                Team
              </button>
            </div> */}
            <div className="flex items-center mb-3 px-3 py-3 bg-white border border-gray-300 rounded-full min-h-[48px]">
              {isSearching ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-500 mr-2 flex-shrink-0"></div>
              ) : (
                <FiSearch
                  className="text-gray-400 mr-2 flex-shrink-0"
                  size={20}
                />
              )}
              <input
                type="text"
                placeholder="Search staff members..."
                className="bg-transparent outline-none flex-1 text-base text-gray-400 placeholder-gray-400"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="h-3" />
            <ul className="flex-1 overflow-y-auto">
              {filteredStaff.length === 0 && search && !isSearching ? (
                <li className="flex items-center justify-center py-8 text-gray-500">
                  <div className="text-center">
                    <FiSearch
                      className="mx-auto mb-2 text-gray-300"
                      size={32}
                    />
                    <p>No staff members found</p>
                    <p className="text-sm">Try a different search term</p>
                  </div>
                </li>
              ) : (
                filteredStaff.map((staff: any) => {
                  const initials = getInitials(
                    staff.profile.first_name,
                    staff.profile.last_name
                  );
                  const isCurrentUser = staff.id === currentUserId;
                  // Find the index in the original staffList for selection
                  const originalIdx = staffList.findIndex(
                    (s: any) => s.id === staff.id
                  );
                  const isSelected = originalIdx === selectedStaffIdx;
                  return (
                    <li
                      key={staff.id}
                      className={`flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer mb-1 transition-colors ${
                        isSelected ? "bg-gray-50 hover:bg-gray-100" : ""
                      }`}
                      onClick={() => {
                        setSelectedStaffIdx(originalIdx);
                        setSelectedTab(0);
                      }}
                    >
                      <span className="relative flex items-center justify-center w-9 h-9 rounded-full bg-[#78C7D1] text-white font-semibold text-base overflow-hidden">
                        {staff.profile.pictureBlob ? (
                          <img
                            src={staff.profile.pictureBlob}
                            alt={`${staff.profile.first_name} ${staff.profile.last_name}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              console.error(
                                `❌ Failed to load pictureBlob for staff ${staff.id}`
                              );
                              // Hide the broken image and show initials
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : staff.profile.picture ? (
                          <img
                            src={staff.profile.picture}
                            alt={`${staff.profile.first_name} ${staff.profile.last_name}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              console.error(
                                `❌ Failed to load direct picture for staff ${staff.id}`
                              );
                              // Hide the broken image and show initials
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : null}
                        {/* Show initials if no image or if image failed to load */}
                        {!staff.profile.pictureBlob &&
                          !staff.profile.picture && <span>{initials}</span>}
                        {/* Dot color logic: green for current user, gray for others */}
                        <span
                          className={`absolute bottom-0 right-0 w-3 h-3 border-2 border-white rounded-full ${
                            isCurrentUser ? "bg-green-500" : "bg-gray-300"
                          }`}
                        ></span>
                      </span>
                      <span className="flex-1 font-medium">
                        {staff.profile.first_name} {staff.profile.last_name}
                      </span>
                      {isCurrentUser && (
                        <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded-full">
                          You
                        </span>
                      )}
                    </li>
                  );
                })
              )}
            </ul>
          </div>
          {/* Tabs and Content */}
          <div className="flex-1 h-full bg-[#F3F5F6] p-6 flex flex-col">
            {selectedStaff && (
              <div className="flex items-center gap-4 mb-4 justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-[#78C7D1] flex items-center justify-center text-white font-semibold text-base overflow-hidden relative">
                    {selectedStaff.profile.pictureBlob ? (
                      <img
                        src={selectedStaff.profile.pictureBlob}
                        alt={selectedStaff.profile.first_name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          console.error(
                            `❌ Failed to load pictureBlob for selected staff ${selectedStaff.id}`
                          );
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : selectedStaff.profile.picture ? (
                      <img
                        src={selectedStaff.profile.picture}
                        alt={selectedStaff.profile.first_name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          console.error(
                            `❌ Failed to load direct picture for selected staff ${selectedStaff.id}`
                          );
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : null}
                    {/* Show initials if no image or if image failed to load */}
                    {!selectedStaff.profile.pictureBlob &&
                      !selectedStaff.profile.picture && (
                        <span>
                          {getInitials(
                            selectedStaff.profile.first_name,
                            selectedStaff.profile.last_name
                          )}
                        </span>
                      )}
                  </div>

                  <div>
                    <h2 className="text-xl font-semibold">
                      {selectedStaff.profile.first_name}{" "}
                      {selectedStaff.profile.last_name}
                    </h2>
                    {selectedStaff.profile.role && (
                      <p className="text-gray-500">
                        {selectedStaff.profile.role}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
                    onClick={handleEditStaff}
                    title="Edit"
                  >
                    <FiEdit2 size={20} />
                  </button>
                  <button
                    className="p-2 rounded-full hover:bg-gray-100 text-red-500"
                    onClick={handleDeleteStaff}
                    title="Delete"
                  >
                    <FiTrash2 size={20} />
                  </button>
                </div>
              </div>
            )}
            <div className="flex gap-6 border-b mb-4">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  className={`pb-2 px-2 border-b-2 text-base font-medium transition-colors duration-150 ${
                    selectedTab === TABS.indexOf(tab)
                      ? "border-green-500 text-green-700"
                      : "border-transparent text-gray-500 hover:text-green-600"
                  }`}
                  onClick={() => setSelectedTab(TABS.indexOf(tab))}
                >
                  {tab}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto">
              {selectedStaff && (
                <div>
                  {selectedTab === 0 && <AboutTab staff={selectedStaff} />}
                  {selectedTab === 1 && (
                    <WorkingHoursTab
                      staff={selectedStaff}
                      onSave={handleSaveWorkingHours}
                    />
                  )}
                  {selectedTab === 2 && <UpdatesTab />}
                </div>
              )}
              {!selectedStaff && <div>No staff selected.</div>}
            </div>
          </div>
        </div>
      </div>
      <AddStaffModal
        isOpen={showAddStaffModal}
        onClose={handleCloseAddStaffModal}
        onStaffAdded={handleStaffAdded}
      />
      {selectedStaff && (
        <EditStaffModal
          isOpen={showEditStaffModal}
          onClose={handleCloseEditStaffModal}
          staff={selectedStaff}
          onStaffUpdated={handleStaffUpdated}
        />
      )}
    </div>
  );
}
